import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

import { ApiService } from "../../ApiService";
import {
	TextField,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	Box,
	Input,
	CircularProgress,
	Skeleton,
	Switch,
	FormControlLabel,
	FormGroup,
	Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { returnFullURL } from "../../../utils/goblaFunctions";

export const VisitasPilaresEdit = ({ companyData, currentBranch }) => {
	// Loading states
	const [isLoading, setIsLoading] = useState(false);
	const [isDataLoading, setIsDataLoading] = useState(true);
	const [hoveredStates, setHoveredStates] = useState([false, false, false, false]);

	// Top paragraph state
	const [productParagraph, setProductParagraph] = useState("");
	const [productParagraphOriginal, setProductParagraphOriginal] = useState("");
	const [isEditingParagraph, setIsEditingParagraph] = useState(false);
	const [tempParagraph, setTempParagraph] = useState("");

	// Pillars state
	const [pillars, setPillars] = useState([
		{
			title: "",
			titleOriginal: "",
			text: "",
			textOriginal: "",
			img_url: null,
			enabled: false,
		},
		{
			title: "",
			titleOriginal: "",
			text: "",
			textOriginal: "",
			img_url: null,
			enabled: false,
		},
		{
			title: "",
			titleOriginal: "",
			text: "",
			textOriginal: "",
			img_url: null,
			enabled: false,
		},
		{
			title: "",
			titleOriginal: "",
			text: "",
			textOriginal: "",
			img_url: null,
			enabled: false,
		},
	]);

	// Modal states
	const [openModal, setOpenModal] = useState(false);
	const [editPillarIndex, setEditPillarIndex] = useState(null);
	const [tempPillar, setTempPillar] = useState({
		title: "",
		text: "",
		img_url: null,
		enabled: false,
	});
	const [previewImage, setPreviewImage] = useState(null);
	const [selectedFile, setSelectedFile] = useState(null);
	const apiUrl = process.env.REACT_APP_API_URL;

	// Load data from companyData when it changes
	useEffect(() => {
		if (companyData && companyData.view_waranty) {
			setIsDataLoading(true);

			// Update paragraph
			if (companyData.waranty_text) {
				setProductParagraph(companyData.waranty_text);
				setProductParagraphOriginal(companyData.waranty_text);
				setTempParagraph(companyData.waranty_text);
			}

			// Update pillars
			const updatedPillars = [...pillars];
			companyData.view_waranty.forEach((item, index) => {
				if (index < updatedPillars.length) {
					updatedPillars[index].title = item.title || "";
					updatedPillars[index].titleOriginal = item.title || "";
					updatedPillars[index].text = item.text || "";
					updatedPillars[index].textOriginal = item.text || "";
					updatedPillars[index].img_url = item.img_url;
					updatedPillars[index].enabled = item.enabled || false;
				}
			});

			setPillars(updatedPillars);
			setIsDataLoading(false);
		}
	}, [companyData]);

	// Hover handling
	const handleHover = (index, isHovered) => {
		setHoveredStates((prev) => {
			const newState = [...prev];
			newState[index] = isHovered;
			return newState;
		});
	};

	// Top paragraph handlers
	const handleSaveParagraph = async () => {
		if (!tempParagraph.trim()) {
			toast.error("El texto no puede quedar vacío.");
			return;
		}

		if (!currentBranch) return;

		setIsLoading(true);

		const updatedData = {
			waranty_text: tempParagraph.trim(),
		};

		try {
			const url = `api/company/update/us?key_branch=${currentBranch}`;
			const payload = {
				method: "PUT",
				body: JSON.stringify(updatedData),
			};

			const response = await ApiService.fetchData(url, payload);

			if (response?.code === 200) {
				// Update local state with new value
				setProductParagraph(tempParagraph.trim());
				setProductParagraphOriginal(tempParagraph.trim());
				setIsEditingParagraph(false);
				toast.success(response.message || "Texto actualizado correctamente.");
			} else {
				toast.error(response?.message || "Error al actualizar el texto.");
				setTempParagraph(productParagraphOriginal); // Revert to original
			}
		} catch (error) {
			setTempParagraph(productParagraphOriginal); // Revert to original
		} finally {
			setIsLoading(false);
		}
	};

	const handleCancelParagraph = () => {
		setTempParagraph(productParagraphOriginal);
		setIsEditingParagraph(false);
	};

	// Pillar handlers
	const handleOpenPillarModal = (index) => {
		setEditPillarIndex(index);
		setTempPillar({
			title: pillars[index].title,
			text: pillars[index].text,
			img_url: pillars[index].img_url,
			enabled: pillars[index].enabled,
		});
		setPreviewImage(null);
		setSelectedFile(null);
		setOpenModal(true);
	};

	const handleToggleEnabled = () => {
		setTempPillar((prev) => ({
			...prev,
			enabled: !prev.enabled,
		}));
	};
	const refreshData = async () => {
		if (!currentBranch) return;

		setIsDataLoading(true);
		try {
			const url = `api/company/us?key_branch=${currentBranch}`;
			const response = await ApiService.fetchData(url, { method: "GET" });

			if (response) {
				if (response.waranty_text) {
					setProductParagraph(response.waranty_text);
					setProductParagraphOriginal(response.waranty_text);
					setTempParagraph(response.waranty_text);
				}
				if (response.view_waranty) {
					const updatedPillars = [...pillars];
					response.view_waranty.forEach((item, index) => {
						if (index < updatedPillars.length) {
							updatedPillars[index].title = item.title || "";
							updatedPillars[index].titleOriginal = item.title || "";
							updatedPillars[index].text = item.text || "";
							updatedPillars[index].textOriginal = item.text || "";
							updatedPillars[index].img_url = item.img_url;
							updatedPillars[index].enabled = item.enabled || false;
						}
					});
					setPillars(updatedPillars);
				}
			}
		} catch (error) {
			toast.error(error);
		} finally {
			setIsDataLoading(false);
		}
	};
	const handleSavePillar = async () => {
		if (!tempPillar.title.trim() || !tempPillar.text.trim()) {
			toast.error("El título y la descripción no pueden quedar vacíos.");
			return;
		}
		if (!currentBranch) return;
		setIsLoading(true);
		try {
			let imageUrl = tempPillar.img_url;
			if (selectedFile) {
				const formData = new FormData();
				formData.append("file", selectedFile);
				const uploadResponse = await ApiService.fetchData(
					"api/upload/file",
					{ method: "POST", body: formData },
					false
				);
				if (uploadResponse?.url) {
					imageUrl = uploadResponse.url;
				} else {
					toast.error("Error al subir la imagen al servidor.");
					setIsLoading(false);
					return;
				}
			}
			const updatedData = {
				view_waranty: pillars.map((_, i) => {
					if (i === editPillarIndex) {
						return {
							title: tempPillar.title.trim(),
							text: tempPillar.text.trim(),
							img_url: imageUrl,
							enabled: tempPillar.enabled,
						};
					}
					return {};
				}),
			};
			const url = `api/company/update/us?key_branch=${currentBranch}`;
			const payload = {
				method: "PUT",
				body: JSON.stringify(updatedData),
			};

			const response = await ApiService.fetchData(url, payload);

			if (response?.code === 200) {
				await refreshData();
				setOpenModal(false);
				toast.success(response.message || "Pilar actualizado correctamente.");
			} else {
				toast.error(response?.message || "Error al actualizar el pilar.");
			}
		} catch (error) {
			toast.error("Ocurrió un error al guardar los cambios.");
		} finally {
			setIsLoading(false);
			setSelectedFile(null);
			setPreviewImage(null);
		}
	};

	const handleCancelPillar = () => {
		setOpenModal(false);
		setTempPillar({
			title: "",
			text: "",
			img_url: null,
			enabled: false,
		});
		setEditPillarIndex(null);
		setPreviewImage(null);
		setSelectedFile(null);
	};

	const handleImageChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			setSelectedFile(file);
			const reader = new FileReader();
			reader.onloadend = () => {
				setPreviewImage(reader.result);
			};
			reader.readAsDataURL(file);
		}
	};

	const handleRemoveImage = () => {
		setPreviewImage(null);
		setSelectedFile(null);
		setTempPillar((prev) => ({
			...prev,
			img_url: null,
		}));
	};

	// Function to generate gradient based on index
	const getGradient = (index) => {
		const gradients = [
			"linear-gradient(90deg, rgba(1,94,234,1) 0%, rgba(0,192,250,1) 100%)",
			"linear-gradient(90deg, rgba(250,204,34,1) 0%, rgba(248,54,0,1) 100%)",
			"linear-gradient(103deg, rgba(124,2,248,1) 59%, rgba(183,1,248,1) 78%)",
			"linear-gradient(103deg, rgba(86,196,0,1) 32%, rgba(239,230,23,1) 78%)",
		];

		return gradients[index % gradients.length];
	};

	return (
		<div>
			<div className="container-pill">
				<h2>GARANTÍA DE NUESTROS PRODUCTOS</h2>
				<div className="container-dots">
					<div className="circle-one" />
					<div className="circle-two" />
					<div className="circle-three" />
					<div className="circle-four" />
				</div>

				{isDataLoading ? (
					<Skeleton variant="text" width="50%" height={100} sx={{ margin: "0 auto" }} />
				) : isEditingParagraph ? (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: "8px",
							width: "100%",
						}}>
						<TextField
							variant="outlined"
							multiline
							minRows={3}
							value={tempParagraph}
							onChange={(e) => setTempParagraph(e.target.value)}
							disabled={isLoading}
							sx={{
								"width": "50%",
								"& .MuiOutlinedInput-root": {
									"& textarea": {
										textAlign: "left",
									},
								},
							}}
						/>
						<div>
							<Button
								variant="contained"
								onClick={handleSaveParagraph}
								disabled={isLoading}
								startIcon={isLoading ? <CircularProgress size={20} /> : null}>
								{isLoading ? "Guardando..." : "Guardar"}
							</Button>
							<Button
								variant="outlined"
								onClick={handleCancelParagraph}
								disabled={isLoading}
								style={{ marginLeft: 8 }}>
								Cancelar
							</Button>
						</div>
					</div>
				) : (
					<></>
					/*<div
						className="container-pill-subtitle"
						style={{ cursor: "pointer" }}
						onClick={() => setIsEditingParagraph(true)}>
						{productParagraph || "Haz clic aquí para agregar texto de garantía."}
					</div>*/
				)}

				<div className="pills-container">
					{isDataLoading ? (
						<>
							{[0, 1, 2, 3].map((index) => (
								<div key={index} className={`pill ${index % 2 === 0 ? "top" : "bottom"}`}>
									<Skeleton variant="rectangular" height={150} />
								</div>
							))}
						</>
					) : (
						pillars.map((pillar, index) => {
							const isTop = index % 2 === 0;
							const isHovered = hoveredStates[index];
							const gradient = getGradient(index);

							return (
								<div
									key={index}
									className={`pill ${isHovered ? "hovered" : ""} ${isTop ? "top" : "bottom"}`}
									onMouseEnter={() => handleHover(index, true)}
									onMouseLeave={() => handleHover(index, false)}
									style={{
										position: "relative",
										opacity: pillar.enabled ? 1 : 0.5, // Reduce opacity if disabled
									}}>
									{/* Edit button */}
									<IconButton
										color="primary"
										onClick={() => handleOpenPillarModal(index)}
										sx={{
											"position": "absolute",
											"top": "-75px",
											"left": "50%",
											"transform": "translateX(-50%)",
											"backgroundColor": "#e0e0e0",
											"&:hover": {
												backgroundColor: "#d5d5d5",
											},
											"zIndex": 2,
										}}>
										<EditIcon />
									</IconButton>

									<div className="colored-section" style={{ background: gradient }} />
									<div className={`circle-wrapper ${isHovered ? "hovered" : ""}`}>
										<div className="circle" style={{ background: "#fff" }}>
											<div className="image-container-pilar" style={{height:'100%'}}>
												<img
													src={returnFullURL(pillar?.img_url == 'null' ? null : pillar?.img_url, "picture")}
													alt="pilar"
													style={{ width: "100%",height:'100%',objectFit:'fill', borderRadius: "50%" }}
												/>
											</div>
										</div>
									</div>
									<div className="text-container">
										<div className={`text-title ${isHovered ? "hovered" : ""}`}>{pillar.title}</div>
										<div className={`content ${isHovered ? "hovered" : ""}`}>{pillar.text}</div>
									</div>
									<div className="number-wrapper" style={{ background: gradient }}>
										<div className="inner-number">{String(index + 1).padStart(2, "0")}</div>
									</div>
								</div>
							);
						})
					)}
				</div>
			</div>

			{/* Modal for editing pillar */}
			<Dialog open={openModal} onClose={handleCancelPillar}>
				<DialogTitle>Editar Pilar</DialogTitle>
				<DialogContent dividers>
					<Box display="flex" flexDirection="column" gap={2} minWidth="100%">
						<TextField
							label="Título del Pilar"
							variant="outlined"
							slotProps={{
								htmlInput: {
									maxLength: 35,
								},
							}}
							value={tempPillar.title || ""}
							onChange={(e) => setTempPillar((prev) => ({ ...prev, title: e.target.value }))}
							disabled={isLoading}
						/>
						<Typography
							variant="caption"
							sx={{
								display: "block",
								textAlign: "right",
								color:
									tempPillar.title.length >= 28
										? tempPillar.title.length >= 35
											? "error.main"
											: "warning.main"
										: "#1b1919",
								mt: 0.5,
								mb: 1,
							}}>
							{tempPillar.title.length}/35 caracteres
						</Typography>
						<TextField
							label="Descripción"
							variant="outlined"
							multiline
							minRows={3}
							slotProps={{
								htmlInput: {
									maxLength: 135,
								},
							}}
							value={tempPillar.text || ""}
							onChange={(e) => setTempPillar((prev) => ({ ...prev, text: e.target.value }))}
							disabled={isLoading}
						/>
						<Typography
							variant="caption"
							sx={{
								display: "block",
								textAlign: "right",
								color:
									tempPillar.text.length >= 115
										? tempPillar.text.length >= 135
											? "error.main"
											: "warning.main"
										: "#1b1919",
								mt: 0.5,
								mb: 1,
							}}>
							{tempPillar.text.length}/135 caracteres
						</Typography>
						{/* Toggle switch for enabled/disabled */}
						<FormGroup>
							<FormControlLabel
								control={
									<Switch
										checked={tempPillar.enabled}
										onChange={handleToggleEnabled}
										disabled={isLoading}
										color="success"
									/>
								}
								label={tempPillar.enabled ? "Activo" : "Inactivo"}
							/>
						</FormGroup>

						{/* Image upload section */}
						{!previewImage && !tempPillar.img_url && (
							<Button
								variant="outlined"
								component="label"
								startIcon={<CloudUploadIcon />}
								disabled={isLoading}>
								Seleccionar Imagen
								<Input
									type="file"
									inputProps={{ accept: "image/*" }}
									onChange={handleImageChange}
									sx={{ display: "none" }}
								/>
							</Button>
						)}

						{/* Show existing image */}
						{!previewImage && tempPillar.img_url && (
							<Box position="relative" width="100%">
								<IconButton
									size="small"
									onClick={handleRemoveImage}
									disabled={isLoading}
									sx={{
										"position": "absolute",
										"top": 4,
										"right": 4,
										"backgroundColor": "red",
										"color": "white",
										"&:hover": {
											backgroundColor: "darkred",
										},
										"zIndex": 2,
									}}>
									<DeleteIcon fontSize="small" />
								</IconButton>
								<img
									src={`${apiUrl}${tempPillar.img_url}`}
									alt="Imagen Actual"
									style={{ width: "100%", borderRadius: 8 }}
								/>
								<Button
									variant="outlined"
									component="label"
									startIcon={<CloudUploadIcon />}
									sx={{ marginTop: 1 }}
									disabled={isLoading}>
									Cambiar Imagen
									<Input
										type="file"
										inputProps={{ accept: "image/*" }}
										onChange={handleImageChange}
										sx={{ display: "none" }}
									/>
								</Button>
							</Box>
						)}

						{/* Show preview of new image */}
						{previewImage && (
							<Box position="relative" width="100%">
								<IconButton
									size="small"
									onClick={handleRemoveImage}
									disabled={isLoading}
									sx={{
										"position": "absolute",
										"top": 4,
										"right": 4,
										"backgroundColor": "red",
										"color": "white",
										"&:hover": {
											backgroundColor: "darkred",
										},
										"zIndex": 2,
									}}>
									<DeleteIcon fontSize="small" />
								</IconButton>
								<img src={previewImage} alt="Vista Previa" style={{ width: "100%", borderRadius: 8 }} />
							</Box>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelPillar} disabled={isLoading}>
						Cancelar
					</Button>
					<Button
						variant="contained"
						onClick={handleSavePillar}
						disabled={isLoading}
						startIcon={isLoading ? <CircularProgress size={20} /> : null}>
						{isLoading ? "Guardando..." : "Guardar"}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};
